<template>
  <div id="full-report-preview">
    <b-container
      id="viewer_assurance"
      :class="preview_mode == true ? 'p-5 bg-white rounded' : 'px-5 py-5 bg-white'"
    >
      <b-card class="px-3">
        <!-- Loading skeleton - 데이터 로딩 중일 때만 표시 -->
        <template v-if="!assurance_list_loaded">
          <div class="py-4">
            <b-skeleton width="60%" height="30px" class="mb-4" />
            <b-skeleton height="20px" class="mb-2" />
            <b-skeleton height="20px" class="mb-2" />
            <b-skeleton height="20px" class="mb-2" />
            <b-skeleton width="90%" height="20px" class="mb-3" />

            <b-skeleton height="20px" class="mb-2 mt-4" />
            <b-skeleton height="20px" class="mb-2" />
            <b-skeleton width="80%" height="20px" class="mb-3" />

            <div class="d-flex justify-content-end mt-5">
              <div class="mr-5">
                <b-skeleton width="135px" height="20px" class="mb-3" />
                <b-skeleton width="135px" height="25px" class="mb-2" />
                <b-skeleton width="135px" height="20px" />
              </div>
            </div>

            <hr />

            <div class="d-flex">
              <b-skeleton width="180px" height="80px" class="mr-4" />
              <b-skeleton width="150px" height="80px" />
            </div>
          </div>
        </template>

        <!-- Assurance content -->
        <template v-else-if="has_valid_assurance_content && is_assurance_deployed">
          <template v-if="lang.toUpperCase() === 'KOR'">
            <md
              v-if="valid_latest_assurance?.report_statement"
              class="statement-content"
              :contents="valid_latest_assurance.report_statement"
              :enable-image-modal="true"
            />
            <div class="pt-5 f-90 d-flex flex-column align-items-end mr-5 no-page-break-inside">
              <div
                v-if="valid_latest_assurance?.report_assurance_date"
                class="mb-4"
                style="width: 135px"
              >
                {{ valid_latest_assurance?.report_assurance_date.slice(0, 4) }}년
                {{ valid_latest_assurance?.report_assurance_date.slice(5, 7) }}월
              </div>
              <div
                class="border-bottom-dark fw-300 pb-1 mb-3 d-flex justify-content-between align-items-end"
                style="width: 135px"
              >
                CEO <span class="fw-500 f-105">배 익 현</span>
              </div>
              <div class="mb-5 text-right" style="width: 135px">(주)퀀티파이드이에스지</div>
            </div>

            <hr />

            <div class="d-flex">
              <b-img
                v-if="valid_latest_assurance && valid_latest_assurance.verification_img_path"
                :src="$store.state.static_host + valid_latest_assurance.verification_img_path"
                class="mr-4"
                style="width: 180px"
              />
              <b-img
                v-else
                src="/static_img/logos/aa1000as.jpg"
                class="mr-4"
                style="width: 180px"
              />
              <b-img src="/static_img/logos/QESG.svg" class="" style="width: 150px" />
            </div>
          </template>

          <template v-else>
            <h4 class="mb-4">Third-party verification opinion</h4>
            <md
              v-if="report_statement_e"
              class="statement-content"
              :contents="report_statement_e"
              :enable-image-modal="true"
            />
            <div class="pt-5 f-90 d-flex flex-column align-items-end mr-5 no-page-break-inside">
              <div class="mb-4" style="width: 128px">
                {{ getEngMonth(valid_latest_assurance?.report_assurance_date.slice(5, 7)) }}
                <span class="ml-1">{{
                  valid_latest_assurance?.report_assurance_date.slice(0, 4)
                }}</span>
              </div>
              <div
                class="border-bottom-dark fw-300 pb-1 mb-3 d-flex justify-content-between align-items-end"
                style="width: 128px"
              >
                CEO <span class="fw-500 f-105">Ikhyun Bae</span>
              </div>
              <div class="mb-5 text-right" style="width: 128px">QuantifiedESG, Inc.</div>
            </div>

            <hr />

            <div class="d-flex">
              <b-img
                v-if="valid_latest_assurance && valid_latest_assurance.verification_img_path"
                :src="$store.state.static_host + valid_latest_assurance.verification_img_path"
                class="mr-4"
                style="width: 180px"
              />
              <b-img
                v-else
                src="/static_img/logos/aa1000as.jpg"
                class="mr-4"
                style="width: 180px"
              />
              <b-img src="/static_img/logos/QESG.svg" class="" style="width: 150px" />
            </div>
          </template>
        </template>

        <template v-else>
          <div class="text-center py-5">
            <b-icon-exclamation-circle font-scale="3" variant="secondary" class="mb-3" />

            <!-- 검증의견서가 작성되지 않은 경우 -->
            <template v-if="!has_assurance_content">
              <h5 class="text-secondary mb-3">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '제3자 검증의견서가 작성되지 않았습니다'
                    : 'Independent Assurance Statement has not been written'
                }}
              </h5>
              <p class="text-muted">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '검증의견서를 작성해주세요.'
                    : 'Please write the assurance statement.'
                }}
              </p>
              <p class="text-muted mt-3">
                {{ lang.toUpperCase() === 'KOR' ? 'QESG로 문의하기: ' : 'Contact QESG: ' }}
                <a href="mailto:info@qesg.co.kr">info@qesg.co.kr</a>
              </p>
            </template>

            <!-- 검증의견서는 작성되었으나 배포되지 않은 경우 -->
            <template v-else>
              <h5 class="text-secondary mb-3">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '제3자 검증의견서가 배포되지 않았습니다'
                    : 'Independent Assurance Statement has not been published'
                }}
              </h5>
              <p class="text-muted">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '검증의견서 배포를 완료해주세요.'
                    : 'Please publish the assurance statement.'
                }}
              </p>
              <p class="text-muted mt-3">
                {{ lang.toUpperCase() === 'KOR' ? 'QESG로 문의하기: ' : 'Contact QESG: ' }}
                <a href="mailto:info@qesg.co.kr">info@qesg.co.kr</a>
              </p>
            </template>
          </div>
        </template>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'ViewAssuranceStatement',
  props: {
    preview_mode: { type: Boolean, default: false }
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapState('assurance', ['assurance_list_loaded', 'assurance_list']),
    ...mapGetters('report_list', ['current_report_code']),
    ...mapGetters('assurance', [
      'latest_assurance',
      'has_assurance_content',
      'is_assurance_deployed'
    ]),
    // Check if the loaded assurance data is for the current report
    isAssuranceDataValid() {
      // 데이터가 로드되었고, 현재 보고서에 대한 검증의견서가 있는지 확인
      if (!this.assurance_list_loaded || !this.current_report_code) {
        return false;
      }
      // latest_assurance가 없으면 검증의견서가 없는 것으로 간주
      if (!this.latest_assurance) {
        return true; // 데이터는 로드되었지만 검증의견서가 없음
      }
      return this.latest_assurance.report_code === this.current_report_code;
    },
    // Get valid latest assurance for current report
    valid_latest_assurance() {
      if (!this.assurance_list_loaded || !this.latest_assurance) {
        return null;
      }
      // report_code가 일치하는 경우만 반환
      if (this.latest_assurance.report_code === this.current_report_code) {
        return this.latest_assurance;
      }
      return null;
    },
    // Check if valid assurance has content
    has_valid_assurance_content() {
      return !!this.valid_latest_assurance?.report_statement;
    },
    report_statement_e() {
      if (!this.assurance_list_loaded || !this.valid_latest_assurance) return '';
      return this.valid_latest_assurance?.report_statement_em
        ? this.valid_latest_assurance.report_statement_em
        : this.valid_latest_assurance?.report_statement_ea;
    }
  },
  methods: {
    getEngMonth(month) {
      if (month == '01') return 'January';
      if (month == '02') return 'February';
      if (month == '03') return 'March';
      if (month == '04') return 'April';
      if (month == '05') return 'May';
      if (month == '06') return 'June';
      if (month == '07') return 'July';
      if (month == '08') return 'August';
      if (month == '09') return 'September';
      if (month == '10') return 'October';
      if (month == '11') return 'November';
      if (month == '12') return 'December';
    }
  }
};
</script>

<style scoped>
.statement-content {
  white-space: normal;
}

.statement-content >>> img {
  max-width: 100%;
}
</style>
